/**
 * Headless BPMN → SVG. The `renderBoardSvg` analog for process diagrams:
 * serializes a `composeBpmn` result into ONE portable `<svg>` string —
 * inline fills/strokes only (resvg resolves no stylesheet), no CSS classes,
 * no `foreignObject`, per-style arrowhead `<marker>`s with EXPLICIT fill
 * (`context-stroke` renders invisible on Safari — same trap the board
 * documents), coordinates rounded to 2dp.
 *
 * Def ids are namespaced by `idPrefix`, sanitized to `[A-Za-z0-9_-]` so the
 * id is always well-formed XML AND a resolvable `url(#…)` funcIRI (the
 * board's `colorKey` precedent). Callers embedding several diagrams in one
 * document must pass DISTINCT prefixes — the default is the constant
 * `"bpmn-"`, so two default-rendered diagrams share marker ids (first def
 * wins).
 */

import { ARROW_MARKER_SIZE, ARROW_OPEN_MARKER_SIZE, ARROW_OPEN_STROKE, ARROW_REF_X, ARROW_REF_Y, ARROW_VIEWBOX } from "./metrics.js";
import { composeBpmn, type BpmnGroup, type BpmnShape, type ComposeBpmnOptions } from "./compose-bpmn.js";
import { defaultProcessTokens } from "./tokens.js";
import type { BpmnSceneInput } from "./types.js";

export interface RenderProcessSvgOptions extends ComposeBpmnOptions {
  /** Canvas backdrop; defaults to the token background. Pass `null` for none. */
  readonly background?: string | null;
  /** Namespace for `<defs>` ids (arrow markers). */
  readonly idPrefix?: string;
}

export const round = (n: number): number => Math.round(n * 100) / 100;

/** Path-marker name → `<defs>` id suffix (namespaced by `idPrefix`). */
export const MARKER_DEF_ID = {
  solid: "arrow",
  open: "arrow-open",
  swArrow: "sw-arrow",
  swFlowTick: "sw-flow-tick",
  swFlowTickErr: "sw-flow-tick-err",
  swCompTick: "sw-comp-tick",
  swReentryTick: "sw-reentry-tick",
} as const;

export function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function attr(name: string, v: string | number | undefined): string {
  if (v === undefined) return "";
  return ` ${name}="${typeof v === "number" ? round(v) : esc(v)}"`;
}

export function strokeAttrs(s: { stroke?: string; strokeWidth?: number; strokeOpacity?: number; dash?: string }): string {
  return (
    attr("stroke", s.stroke) +
    attr("stroke-width", s.strokeWidth) +
    attr("stroke-opacity", s.strokeOpacity) +
    attr("stroke-dasharray", s.dash)
  );
}

export function shapeSvg(s: BpmnShape, fontFamily: string, idPrefix: string): string {
  switch (s.kind) {
    case "rect":
      return (
        `<rect${attr("x", s.x)}${attr("y", s.y)}${attr("width", s.w)}${attr("height", s.h)}` +
        `${attr("rx", s.rx)}${attr("fill", s.fill)}${strokeAttrs(s)}/>`
      );
    case "circle":
      return `<circle${attr("cx", s.cx)}${attr("cy", s.cy)}${attr("r", s.r)}${attr("fill", s.fill)}${strokeAttrs(s)}/>`;
    case "polygon":
      return `<polygon${attr("points", s.points)}${attr("fill", s.fill)}${strokeAttrs(s)}/>`;
    case "path": {
      const marker = s.marker ? attr("marker-end", `url(#${idPrefix}${MARKER_DEF_ID[s.marker]})`) : "";
      return `<path${attr("d", s.d)}${attr("fill", s.fill)}${strokeAttrs(s)}${attr("opacity", s.opacity)}${marker}/>`;
    }
    case "line":
      return `<line${attr("x1", s.x1)}${attr("y1", s.y1)}${attr("x2", s.x2)}${attr("y2", s.y2)}${strokeAttrs(s)}/>`;
    case "text": {
      // Committed overlay-label halo: dark stroke painted UNDER the glyphs.
      const halo = s.halo
        ? ` paint-order="stroke"${attr("stroke", s.halo)} stroke-width="3" stroke-linejoin="round"`
        : "";
      const rotate = s.rotate
        ? ` transform="rotate(${round(s.rotate.deg)} ${round(s.rotate.cx)} ${round(s.rotate.cy)})"`
        : "";
      return (
        `<text${attr("x", s.x)}${attr("y", s.y)}${attr("fill", s.fill)}` +
        `${attr("font-family", fontFamily)}${attr("font-size", s.size)}` +
        `${attr("font-weight", s.weight)}${s.italic ? ` font-style="italic"` : ""}` +
        `${s.anchor ? ` text-anchor="${s.anchor}"` : ""}${halo}${rotate}>` +
        esc(s.text) +
        `</text>`
      );
    }
  }
}

export function groupSvg(g: BpmnGroup, fontFamily: string, idPrefix: string): string {
  const attrs = Object.entries(g.attrs)
    .map(([k, v]) => attr(k, v))
    .join("");
  const title = g.title ? `<title>${esc(g.title)}</title>` : "";
  return (
    `<g${attrs}${attr("opacity", g.opacity)}>` +
    title +
    g.shapes.map((s) => shapeSvg(s, fontFamily, idPrefix)).join("") +
    `</g>`
  );
}

/** Solid sequence-flow arrowhead — explicit fill, never `context-stroke`. */
function arrowMarker(idPrefix: string, fill: string): string {
  return (
    `<marker${attr("id", `${idPrefix}arrow`)} viewBox="0 0 ${ARROW_VIEWBOX} ${ARROW_VIEWBOX}"${attr("refX", ARROW_REF_X)}${attr("refY", ARROW_REF_Y)}` +
    `${attr("markerWidth", ARROW_MARKER_SIZE)}${attr("markerHeight", ARROW_MARKER_SIZE)} orient="auto">` +
    `<path d="M 0 0 L 10 5 L 0 10 z"${attr("fill", fill)}/></marker>`
  );
}

/** Hollow open head (comp/drop/reentry flows) — explicit stroke. */
function arrowOpenMarker(idPrefix: string, stroke: string): string {
  return (
    `<marker${attr("id", `${idPrefix}arrow-open`)} viewBox="0 0 ${ARROW_VIEWBOX} ${ARROW_VIEWBOX}"${attr("refX", ARROW_REF_X)}${attr("refY", ARROW_REF_Y)}` +
    `${attr("markerWidth", ARROW_OPEN_MARKER_SIZE)}${attr("markerHeight", ARROW_OPEN_MARKER_SIZE)} orient="auto">` +
    `<path d="M 0 0 L 10 5 L 0 10" fill="none"${attr("stroke", stroke)}${attr("stroke-width", ARROW_OPEN_STROKE)}/></marker>`
  );
}

/** Ids feed both `id="…"` and `url(#…)`: restrict to funcIRI-safe chars —
 *  escaping alone can't fix whitespace/`#` inside a funcIRI. */
export function sanitizeIdPrefix(p: string): string {
  return p.replace(/[^A-Za-z0-9_-]/g, "");
}

export function renderProcessSvg(input: BpmnSceneInput, opts: RenderProcessSvgOptions = {}): string {
  const tokens = opts.tokens ?? defaultProcessTokens();
  const idPrefix = sanitizeIdPrefix(opts.idPrefix ?? "bpmn-");
  const composed = composeBpmn(input, { tokens, ownerColor: opts.ownerColor });

  const w = Math.max(1, Math.round(composed.width));
  const h = Math.max(1, Math.round(composed.height));

  const defs: string[] = [];
  if (composed.markers.solid) defs.push(arrowMarker(idPrefix, tokens.arrowFill));
  if (composed.markers.open) defs.push(arrowOpenMarker(idPrefix, tokens.arrowFill));

  const bgColor = opts.background === undefined ? tokens.background : opts.background;
  const bg = bgColor ? `<rect x="0" y="0"${attr("width", w)}${attr("height", h)}${attr("fill", bgColor)}/>` : "";

  const body = [...composed.frames, ...composed.edges, ...composed.nodes]
    .map((g) => groupSvg(g, tokens.fontFamily, idPrefix))
    .join("");
  // Lane rail: rail chrome keeps canvas x (full-width separators / pool
  // borders); grid content shifts right by the rail width. Laneless output
  // stays byte-identical to the pre-lane renderer.
  const rail = composed.rail.length
    ? composed.rail.map((g) => groupSvg(g, tokens.fontFamily, idPrefix)).join("")
    : "";
  const railT = composed.railWidth ? `translate(${round(composed.railWidth)},${round(composed.headroom)})` : composed.headroom ? `translate(0,${round(composed.headroom)})` : "";
  const railWrapped = rail ? (composed.headroom ? `<g transform="translate(0,${round(composed.headroom)})">${rail}</g>` : rail) : "";
  const content = railWrapped + (railT ? `<g transform="${railT}">${body}</g>` : body);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img">` +
    (defs.length ? `<defs>${defs.join("")}</defs>` : "") +
    bg +
    content +
    `</svg>`
  );
}
